import React from "react";
import PropTypes from "prop-types";
import { Row, Col, Card, Table } from "react-bootstrap";
import ProgressBar from "../ProgressBar";

const OneTimeCostRow = ({ oneTimeCostDetailData, oneTimeCost, propertyType, isTaxeringsvardeMissing, isPantbrevMissing, onOpenSettings }) => {

    const formatCurrency = (value) => {
        if (isNaN(value)) return value;
        return new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK" })
            .format(value)
            .replace("kr", "")
            .trim();
    };

    // Don't render anything if no data for Villa
    if (propertyType === "villa" && oneTimeCostDetailData.length === 0) {
        return null;
    }

    // For Villa - show calculated costs with chart
    if (propertyType === "villa") {
        return (
            <Card>
                <Card.Body>
                    <Card.Title>Engångskostnader - Stämpelskatt & Lagfart</Card.Title>
                    {(isTaxeringsvardeMissing || isPantbrevMissing) && (
                        <div style={{
                            backgroundColor: '#FFF3CD',
                            padding: '1rem',
                            borderRadius: '6px',
                            borderLeft: '4px solid #FFC107',
                            marginBottom: '1rem'
                        }}>
                            <p style={{
                                fontSize: '0.85rem',
                                color: '#664D03',
                                margin: 0,
                                lineHeight: '1.5',
                                fontWeight: '600',
                                marginBottom: '0.5rem'
                            }}>
                                ⚠️ Beräkningen är inte korrekt
                            </p>
                            <p style={{
                                fontSize: '0.85rem',
                                color: '#664D03',
                                margin: 0,
                                lineHeight: '1.5'
                            }}>
                                {isTaxeringsvardeMissing && !isPantbrevMissing && 'Taxeringsvärdet saknas'}
                                {isPantbrevMissing && !isTaxeringsvardeMissing && 'Pantbrevsbeloppet saknas'}
                                {isTaxeringsvardeMissing && isPantbrevMissing && 'Taxeringsvärdet och pantbrevsbeloppet saknas'}
                                {' '}vilket gör att engångskostnaderna inte kan beräknas korrekt. {' '}
                                <button
                                    onClick={() => onOpenSettings('pantbrev')}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#664D03',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        padding: 0,
                                        font: 'inherit'
                                    }}
                                >
                                    Lägg till värden här
                                </button>
                            </p>
                        </div>
                    )}
                    <p>Tabellen visar stämpelskatt och lagfartskostnader som tillkommer vid köp av villa/äganderätt i Sverige. Kom ihåg att dessa engångskostnader oftast inte kan finansieras med bolån, utan måste betalas utöver din kontantinsats enligt svenska regler.</p>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th width="35%"></th>
                                <th>Summa</th>
                            </tr>
                        </thead>
                        <tbody>
                            {oneTimeCostDetailData.map((item, index) => (
                                <tr key={index}>
                                    <td>{item.name}</td>
                                    <td><b>{item.value} kr</b><br /><i><small>{item.description}</small></i></td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        );
    }

    // For Bostadsrätt - show potential costs (informational only)
    if (propertyType === "bostadsratt") {
        return (
            <Card>
                <Card.Body>
                    <Card.Title>Möjliga BRF-avgifter Bostadsrätt - Överlåtelse & Pantsättning</Card.Title>
                    <div className="alert alert-info mb-3" role="alert">
                        <strong>OBS:</strong> Dessa BRF-avgifter varierar mellan olika bostadsrättsföreningar och anges sällan i annonser. Kontakta styrelsen eller förvaltaren för aktuella avgifter i din specifika förening.
                    </div>
                    
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th width="25%">Avgift</th>
                                <th width="25%">Typisk kostnad</th>
                                <th>Beskrivning</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Överlåtelseavgift</strong></td>
                                <td><span className="text-muted">5 00 - 5 000 kr</span></td>
                                <td><small>Avgift som föreningen tar ut vid försäljning av bostadsrätt. Varierar mellan föreningar, men många föreningar utgår från en avgift på omkring 2,5% av ett prisbasbelopp.</small></td>
                            </tr>
                            <tr>
                                <td><strong>Pantsättningsavgift</strong></td>
                                <td><span className="text-muted">500 - 3 000 kr</span></td>
                                <td><small>Avgift för att pantsätta bostadsrätten som säkerhet för lån. Tas ut av vissa föreningar och får inte vara mer än 1 % av prisbasbeloppet per pant.</small></td>
                            </tr>
                        </tbody>
                    </Table>
                    
                    <div className="mt-3">
                        <h6>Tips för bostadsrättsköpare:</h6>
                        <ul className="mb-0">
                            <li>Fråga mäklaren eller föreningen om eventuella avgifter</li>
                            <li>Begär ut föreningens stadgar och avgiftsregler</li>
                            <li>Budgetera för 1 000 - 7 000 kr i möjliga extra avgifter</li>
                            <li>Vissa föreningar har inga extra avgifter alls</li>
                        </ul>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    return null;
}

// Requirements and export

const oneTimeCostDetailDataPropTypes = PropTypes.arrayOf(
    PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
    })
);

const oneTimeCostPropTypes = PropTypes.arrayOf(
    PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
        color: PropTypes.string.isRequired,
    })
);

OneTimeCostRow.propTypes = {
    oneTimeCostDetailData: oneTimeCostDetailDataPropTypes,
    oneTimeCost: oneTimeCostPropTypes,
    propertyType: PropTypes.string,
    isTaxeringsvardeMissing: PropTypes.bool,
    isPantbrevMissing: PropTypes.bool,
    onOpenSettings: PropTypes.func
};

export default OneTimeCostRow;